<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useExerciseStore } from '@/store/exercises';
import CategoryBubble from '@/components/CategoryBubble.vue';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import { useLocale } from '@/plugins/translation';
import type { SubjectVideo } from '@ilteducation/videos';
import { storeToRefs } from 'pinia';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { areas } = useAreas();
const { byId: getSubjectById } = useCategories();
const exerciseStore = useExerciseStore();
const { exercises } = storeToRefs(exerciseStore);

const props = defineProps<{
  video: SubjectVideo;
}>();

const quizWithProgress = computed(() =>
  (exercises.value[props.video.resourceId] || []).filter((e) => e.type === 'quiz'),
);

const currentSubjectId = ref(props.video.subjectId);
const currentSubject = computed(() => getSubjectById.value(currentSubjectId.value || ''));
const currentArea = computed(() => areas.value.find((area) => area.categoryId === currentSubjectId.value));

const { t, l } = useLocale();
onMounted(async () => {
  await exerciseStore.getExercisesWithProgress(props.video.resourceId);
});
</script>

<template>
  <v-hover>
    <template #default="{ isHovering, props: hoverProps }">
      <v-card v-if="video" v-bind="hoverProps" class="ma-0 pa-0 overflow-visible" :elevation="isHovering ? 8 : 2">
        <router-link
          :to="{
            name: 'showFilm',
            params: {
              subject: currentSubject?.slug,
              area: currentArea?.slug,
              film: video.slug,
            },
          }"
        >
          <v-img :src="video.thumbnail" alt="" class="rounded" />
          <CategoryBubble v-if="currentSubject?.categoryCode" :subject="currentSubject" size="small" />
          <v-card-text v-if="video" class="pa-3">
            <span class="film-title">{{ video.title }}</span>
            <div v-for="(quiz, index) in quizWithProgress" :key="index" class="my-0 pa-0">
              <v-tooltip :disabled="!quiz.progress?.locked" location="right">
                <template #activator="{ props: tooltipProps }">
                  <div v-bind="tooltipProps">
                    <v-btn
                      id="no-background-hover"
                      block
                      tabindex="-1"
                      color="#ffffff"
                      elevation="0"
                      class="quiz-progress-button no-uppercase my-1"
                      :disabled="quiz.progress?.locked"
                      aria-hidden="true"
                    >
                      <b>Quiz {{ index + 1 }}</b>
                      <v-spacer></v-spacer>
                      <FontAwesomeIcon
                        class="ml-2"
                        :icon="
                          quiz.progress?.completed
                            ? 'fa-solid fa-check-circle'
                            : quiz.progress?.locked
                              ? 'fa-solid fa-lock'
                              : ''
                        "
                      />
                    </v-btn>
                  </div>
                </template>
                <span>
                  {{ t(l.quiz.locked_quiz_explanation) }}
                </span>
              </v-tooltip>
            </div>
          </v-card-text>
        </router-link>
      </v-card>
    </template>
  </v-hover>
</template>

<style lang="scss" scoped>
.category-bubble {
  position: absolute;
  top: 12px;
  left: -12px;
  width: 32px;
}
#no-background-hover::before {
  background-color: transparent !important;
}
.no-uppercase {
  text-transform: unset !important;
}

.film-title {
  font-weight: 300;
  font-family: 'filson-soft', Helvetica, Arial, 'Lucida Grande', sans-serif;
  font-size: rem-calc(14);
  letter-spacing: 0.1px;
  color: #005eb8;
}
</style>
